import { LOCATION_CHANGE } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import _pick from 'lodash/pick';
import _get from 'lodash/get';
import qs from 'qs';

import endpoints from '../../../config/endpoints';

import intl from '../../shared/intl';
import http from '../../shared/http';
import createTable from '../components/table/invalid';
import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createGroups, { getGroupValues } from '../../shared/groups';
import getRequestQuery from '../shared/get-invalid-request-query';
import checkPermissions from '../../shared/check-permissions';
import getFilterContext from '../shared/get-filters-context';
import { getDateRangeFromCalendar } from '../../shared/calendar';

const table = createTable({
	namespace: 'invalid',
	stateNodeName: 'table'
});

const calendar = createCalendar({
	namespace: 'invalid',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'invalid',
	stateNodeName: 'filters',
	queryBuildFunc: getRequestQuery,
	listOfFilters: [{
		name: 'cancellation_reasons',
		title: 'Cancellation reasons',
		placeholder: 'Add reason',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.conversion-cancellation-reasons'
		}
	}, {
		name: 'status',
		title: 'Status',
		placeholder: 'Add status',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.conversion-statuses'
		}
	}, {
		name: 'category',
		title: 'Category',
		placeholder: 'Add category',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS_CATEGORIES
		}
	}, {
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'offers',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'campaigns',
		title: 'Campaigns',
		placeholder: 'Add campaigns',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.CAMPAIGNS
		}
	}, {
		name: 'locations',
		title: 'Locations',
		placeholder: 'Add locations',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
		}
	}, {
		name: 'admins',
		title: 'Admins',
		placeholder: 'Add admins',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	}, {
		name: 'sids1',
		title: 'sids1',
		placeholder: 'Add sids1',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SIDS1
		}
	}, {
		name: 'sids2',
		title: 'sids2',
		placeholder: 'Add sids2',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SIDS2
		}
	}, {
		name: 'sids3',
		title: 'sids3',
		placeholder: 'Add sids3',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SIDS3
		}
	}, {
		name: 'sids4',
		title: 'sids4',
		placeholder: 'Add sids4',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SIDS4
		}
	}, {
		name: 'sids5',
		title: 'sids5',
		placeholder: 'Add sids5',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.SIDS5
		}
	}]
});

const groups = createGroups({
	namespace: 'invalid',
	stateNodeName: 'groups'
});

// actions types
const FETCH_DATA_SUCCESS = 'invalid/fetch-data-success';
const RESET_TO_DEF = 'invalid/RESET_TO_DEF';
const FETCH_DATA_ERROR = 'invalid/fetch-data-error';
const APPLY = 'invalid/apply';
const SELECT_CURRENCY = 'invalid/select-currency';
const SHOW_PROGRESS_BAR = 'invalid/show-progress-bar';
const TOGGLE_SORT_MAIN = 'invalid/toggle-sort-main';
const TOGGLE_SORT_ADDITIONAL = 'invalid/toggle-sort-additional';
const TOGGLE_SORT_INTERNAL = 'invalid/toggle-sort-internal';
const TOGGLE_PROGRESS_BAR = 'invalid/TOGGLE_PROGRESS_BAR';

const SUBMIT_SUCCESS = 'invalid/submit-success';
const SUBMIT_ERROR = 'invalid/submit-error';

// permissions
export const allowedPermissions = {
	'view': 'analytics_view',
	'autotrash_view': 'autotrash_view'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	filters: filters.initialState,
	groups: groups.initialState,
	showProgressBar: false,
	table: table.initialState,
	query: {
		groups: ['date'],
		date_range: {
			start: null,
			end: null
		}
	},
	conversionsFrom: 1,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: props => async (dispatch, getState) => {
		let externalQuery = await getExternalQuery(props.location, dispatch);
		dispatch(actions.showProgressBar());

		dispatch(actions.fetchRequest(externalQuery));
	},

	fetchRequest: (externalQuery = {}, deferred = false) => (dispatch, getState) => {
		const query = getRequestQuery(getState().invalid);
		let url = `${endpoints.STATISTICS.INVALID}?${query}`;

		return (http({
			url,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			],
			addToResponse: {
				externalQuery,
				currentQuery: qs.parse(query)
			}
		}))(dispatch);
	},

	apply: () => (dispatch, getState) => {
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},

	clear: () => (dispatch, getState) => {
		dispatch(actions.showProgressBar());
		dispatch({ type: RESET_TO_DEF });
		dispatch(actions.fetchRequest());
	},

	selectCurrency: id => dispatch => {
		dispatch({
			type: SELECT_CURRENCY,
			payload: id
		});

		dispatch(actions.apply());
	},

	showProgressBar: () => ({
		type: SHOW_PROGRESS_BAR
	}),
	toggleSortMain: (payload) => dispatch => {
		dispatch({
			type: TOGGLE_SORT_MAIN,
			payload
		});
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},
	toggleSortAdditional: (payload) => dispatch => {
		dispatch({
			type: TOGGLE_SORT_ADDITIONAL,
			payload
		});
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},
	toggleSortInternal: (payload) => dispatch => {
		dispatch({
			type: TOGGLE_SORT_INTERNAL,
			payload
		});
	},
	addSpecificGrouping: () => dispatch => {
		dispatch(groups.actions.groupsSelectValue(
			'secondGroups', [{ 'value': 'cancellation_reason', 'label': 'Cancellation reason'}]
		));
		dispatch(actions.apply());
	}
},
calendar.actions,
filters.actions,
groups.actions,
table.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		return { ...initialState };
	},
	[RESET_TO_DEF]: (state, action) => {
		return { ...initialState };
	},
	[TOGGLE_SORT_MAIN]: (state, action) => {
		const { index, direction } = state.table.sortMain || {};

		return {
			...state,
			table: {
				...state.table,
				sortMain: {
					index: action.payload,
					direction: index === action.payload && direction === 'asc' ? 'desc' : 'asc'
				}
			}
		};
	},
	[TOGGLE_SORT_ADDITIONAL]: (state, action) => {
		const { index, direction } = state.table.sortAdditional || {};

		return {
			...state,
			showProgressBar: false,
			table: {
				...state.table,
				sortAdditional: {
					index: action.payload,
					direction: index === action.payload && direction === 'asc' ? 'desc' : 'asc'
				}
			}
		};
	},
	[TOGGLE_SORT_INTERNAL]: (state, action) => {
		const { index, direction } = state.table.sortInternal || {};

		return {
			...state,
			showProgressBar: false,
			table: {
				...state.table,
				sortInternal: {
					index: action.payload,
					direction: index === action.payload && direction === 'asc' ? 'desc' : 'asc'
				}
			}
		};
	},
	[FETCH_DATA_SUCCESS]: (state, action) => {
		let data = action.payload.data;

		return {
			...state,
			data,
			showProgressBar: false,
			error: false
		};
	},
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[APPLY]: (state, action) => ({
		...state,
		conversionsFrom: action.payload.conversionsFrom,
		query: {
			...state.query,
			groups: getGroupValues(
				state.groups.listOfControls,
				state.groups.orderControlsDuration
			)
		}
	}),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[TOGGLE_PROGRESS_BAR]: state => ({...state, showProgressBar: !state.showProgressBar })
},
calendar.reducers,
filters.reducers,
groups.reducers,
table.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.invalid.data,
	getTable: table.selectors.getData,
	getSort: table.selectors.getSort,
	getGroupColumns: table.selectors.getGroupColumns,
	getIntl: intl,
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.invalid.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.invalid.filters,
		state => state
	)(state),
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getCurrentCurrency: state => state.invalid.query.currency,
	getGroupsSelectControls: groups.selectors.getControls,
	getGroupsSelectOptions: groups.selectors.getOptions,
	getGroupsSelectValues: state => getGroupValues(groups.selectors.getControls(state)),
	getProgressBar: state => state.invalid.showProgressBar,
	getQueryToConversionsSimple: state => getQueryToConversionsSimple(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getConversionsFilterInitialValues: state => ({
		conversionsFrom: state.invalid.conversionsFrom
	}),
	getFilterContext: state => getFilterContext(state.invalid, {
		date_range: {
			start: moment().format('YYYY-MM-DD'),
			end: moment().format('YYYY-MM-DD')
		}
	}),
	getError: state => state.invalid.error
};

const getQueryToConversionsSimple = state => (row, statusesIds) => {
	let date_range = getDateRangeFromCalendar(selectors.getCalendar(state));
	if (!row) return null;

	const statusesFilter = {
		name: 'statuses',
		values: statusesIds.map(stat => ({ value: stat, label: stat }))
	};
	const { filters: { selectedFilters }, groups: { listOfControls } } = state.invalid;

	const [ firstGroup, secondGroup ] = listOfControls.map(loc => _get(loc, 'value[0].value'));

	if (secondGroup === 'date' || firstGroup === 'date') {
		date_range = {
			start: `${row['date']} 00:00:00.000`,
			end: `${row['date']} 23:59:59.999`
		};

	}

	const filters = [ statusesFilter ].concat(selectedFilters.map(item => _pick(item, ['values', 'name'])));

	const query = {
		date_range,
		filters: filters.reduce((acc, val) => {

			if (val.name && val.values && val.values.length) {
				acc[val.name] = val.values.map(vl => vl.value);
			}

			return acc;
		}, {})
	};

	const entity = {
		filters,
		date_range: query.date_range
	};

	return qs.stringify({ query, entity });
};

async function getExternalQuery(location, dispatch) {
	const {
		mailings, type, popup, event, ...externalQuery
	} = (location && location.query && qs.parse(location.query)) || {};

	if (((mailings && type) || popup) && event) {
		let url = null;

		if (mailings) {
			url = `${endpoints.MAILING_EVENTS.AFFILIATES}?${qs.stringify({ mailings: mailings.split(','), type, event })}`;
		}

		if (popup) {
			url = `${endpoints.CUSTOM_POPUPS.AFFILIATES}?${qs.stringify({ popup_id: popup, event })}`;
		}

		const request = http({
			url,
			method: 'get',
			actions: [SUBMIT_SUCCESS, SUBMIT_ERROR]
		});

		const action = await request(dispatch);

		if (action.type === SUBMIT_SUCCESS) {
			const affiliates = action.payload.data;

			externalQuery.entity = {
				filters: [{
					name: 'affiliates',
					values: affiliates.map((affiliate) => ({
						value: affiliate.id,
						label: affiliate.email
					}))
				}],
				groups: ['affiliate']
			};

			externalQuery.query = {
				filters: {
					affiliates: affiliates.map((affiliate) => affiliate.id)
				},
				groups: ['affiliate']
			};
		}

		if (action.type === SUBMIT_ERROR) {
			return null;
		}
	}

	return externalQuery;
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/invalid/index.js